import { useState, useEffect, useContext } from "react"
import { AuthContext } from "../context/AuthContext"
import OrderDetailsModal from "../components/OrderDetailsModal"
import axios from "axios"
import { format } from "date-fns"
import { Eye, Search, Package, CheckCircle } from "lucide-react"
import { showToast } from "../utils/toastUtils"

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const NewOrders = () => {
  const { token } = useContext(AuthContext)
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedOrderId, setSelectedOrderId] = useState(null)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  // Reusable Input Style (matches CreateOrder)
  const inputStyle = "w-full rounded-lg border-gray-200 bg-white px-4 py-2.5 text-gray-700 placeholder-gray-400 shadow-sm transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none hover:border-blue-300"

  if (token) {
    axios.defaults.headers.common["x-auth-token"] = token
  }

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true)
        const res = await axios.get(`${BASE_URL}/api/orders`)
        const newOrders = res.data.filter(order => order.status === "New")
        setOrders(newOrders)
        setError(null)
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching orders")
        showToast("Error", err.response?.data?.message || "Error fetching orders", "error")
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()
  }, [token])

  const filteredOrders = orders.filter(order => {
    const lower = searchTerm.toLowerCase()
    return (
      order.customerName.toLowerCase().includes(lower) ||
      order.phone.includes(searchTerm) ||
      (order.trackingNumber && order.trackingNumber.toLowerCase().includes(lower)) ||
      (order.courierService && order.courierService.toLowerCase().includes(lower)) ||
      (order.orderId && order.orderId.toLowerCase().includes(lower))
    )
  })

  const handleViewOrderDetails = (orderId) => {
    setSelectedOrderId(orderId)
    setIsDetailsModalOpen(true)
  }

  const handleOrderUpdated = (orderId) => {
    setOrders((prev) => prev.filter(order => order._id !== orderId))
  }

  const formatAddress = (order) => {
    const parts = [order.addressLine1, order.addressLine2, order.city].filter(Boolean)
    return parts.join(", ")
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Package className="w-8 h-8 text-blue-600" /> New Orders
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              View and manage newly placed orders.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
              {filteredOrders.length} New
            </span>
          </div>
        </div>

        {/* Alert for Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <span className="font-bold">Error:</span> {error}
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6 max-w-md">
          <div className="relative group">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              className={`${inputStyle} pl-10`}
              placeholder="Search by order ID, name, phone, or tracking..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
              <p className="text-gray-500">Loading new orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-4">
              <div className="bg-gray-100 rounded-full p-4 mb-4">
                <CheckCircle className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">No New Orders</h3>
              <p className="text-gray-500 mt-1 max-w-sm">
                You're all caught up! Check back later for new incoming orders.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Order ID</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Address</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Courier</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <tr key={order._id} className="hover:bg-blue-50/30 transition-colors duration-150 group">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">#{order.orderId || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(order.createdAt), "MMM d, yyyy")}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">{order.customerName}</span>
                          <div className="flex flex-col mt-0.5">
                            <span className="text-xs text-gray-500">{order.phone}</span>
                            {order.phone2 && <span className="text-xs text-gray-400">{order.phone2}</span>}
                          </div>
                          <span className="text-[10px] text-gray-400 uppercase mt-1 tracking-wide">
                            {order.orderBy}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 line-clamp-2" title={formatAddress(order)}>
                          {formatAddress(order)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-gray-900">
                          Rs. {order.total.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700">{order.courierServiceId?.name || "-"}</span>
                          {order.trackingNumber && (
                            <span className="text-xs text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded w-fit mt-1">
                              {order.trackingNumber}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewOrderDetails(order._id)}
                          className="p-2 bg-white border border-gray-200 rounded-lg text-blue-600 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-all shadow-sm group-hover:shadow-md"
                          title="View Order Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      <OrderDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        orderId={selectedOrderId}
        token={token}
        onOrderUpdated={handleOrderUpdated}
      />


    </div>
  )
}

export default NewOrders
